/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { ActionsApi, NodesApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

/**
 * How a composed email leaves the application. The browser cannot speak SMTP
 * itself, so every real send goes through one of these:
 *
 * - `mailto`   — no delivery at all: the draft opens in the user's mail app.
 * - `alfresco` — the repository's `mail` action (`POST /action-executions`).
 *   Only works where the server's own outbound SMTP (`mail.host` in
 *   alfresco-global.properties) is configured — that value is bootstrap
 *   configuration and cannot be set through this app.
 * - `emailjs`  — the EmailJS REST API, callable straight from the browser
 *   with a service/template/public-key triple.
 * - `webhook`  — any HTTPS endpoint of the organisation's own that accepts a
 *   JSON `{ to, subject, body, ...vars }` post and relays it.
 */
export type MailMode = 'mailto' | 'alfresco' | 'emailjs' | 'webhook';

export interface MailConfig {
  mode: MailMode;
  /** Shown as the sender label where the channel supports it. */
  fromName: string;
  emailjs: { serviceId: string; templateId: string; publicKey: string };
  webhook: { url: string; token: string };
}

export interface MailRequest {
  to: string;
  subject: string;
  body: string;
  /** Extra values handed to the channel — `{link}`, `{name}`, the node id. */
  vars?: Record<string, string>;
}

export const MAIL_MODES: MailMode[] = ['mailto', 'alfresco', 'emailjs', 'webhook'];

/**
 * Mail delivery settings and the send path behind them. The configuration is
 * a JSON document under `Data Dictionary/ACA/mail.json` — the same place the
 * watermark and menu-permission settings live — so administrators set it once
 * in Settings → Email templates and every user sends through it.
 *
 * The webhook's token is the exception: that document is readable by every
 * signed-in user, so the token lives on the jobs sidecar instead, which also
 * does the sending. `mail.json` keeps only the webhook's address, for display.
 */
@Injectable({ providedIn: 'root' })
export class MailService {
  private readonly api = inject(AlfrescoApiService);
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthenticationService);

  /** A token found in an older mail.json — handed to the sidecar on the next save. */
  private legacyToken = '';

  private static readonly FOLDER_PATH = 'Data Dictionary/ACA';
  private static readonly FILE_NAME = 'mail.json';
  private static readonly NODE_PATH = `${MailService.FOLDER_PATH}/${MailService.FILE_NAME}`;

  static readonly DEFAULT: MailConfig = {
    mode: 'mailto',
    fromName: '',
    emailjs: { serviceId: '', templateId: '', publicKey: '' },
    webhook: { url: '', token: '' }
  };

  readonly settings = signal<MailConfig>(MailService.DEFAULT);

  /** The signed-in person's address — the test-send target. */
  readonly viewer = signal<{ name: string; email: string }>({ name: '', email: '' });

  private loaded = false;

  load(): void {
    if (this.loaded) {
      return;
    }
    this.loaded = true;

    this.read()
      .pipe(catchError(() => of(null)))
      .subscribe((saved) => {
        if (saved) {
          this.settings.set(saved);
        }
      });

    const instance: any = this.api.getInstance();
    from(instance.contentClient.callApi('/people/-me-', 'GET', {}, {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>)
      .pipe(
        map((response: any) => response?.entry),
        catchError(() => of(null))
      )
      .subscribe((entry: any) => {
        if (entry) {
          this.viewer.set({
            name: entry.displayName || [entry.firstName, entry.lastName].filter(Boolean).join(' ') || entry.id,
            email: entry.email || ''
          });
        }
      });
  }

  save(config: MailConfig): Observable<void> {
    const clean = MailService.sanitize(config);
    const token = clean.webhook.token || this.legacyToken;
    const stored: MailConfig = { ...clean, webhook: { url: clean.webhook.url, token: '' } };
    // the token goes to the sidecar first, so a failure there never leaves
    // mail.json pointing at a webhook nothing can authenticate to
    const relay =
      clean.mode === 'webhook'
        ? this.http.post(`${this.jobsUrl}/api/mail/webhook`, { url: clean.webhook.url, token }, { headers: this.credentials() })
        : of(null);
    return relay.pipe(
      switchMap(() => this.nodeId(true)),
      switchMap((nodeId) => from(this.nodes().updateNodeContent(nodeId, JSON.stringify(stored)) as Promise<any>)),
      tap(() => {
        this.settings.set(stored);
        if (clean.mode === 'webhook') {
          this.legacyToken = '';
        }
      }),
      map(() => undefined)
    );
  }

  /** What the sidecar holds for the webhook — administrators only. */
  webhookStatus(): Observable<{ url: string; hasToken: boolean } | null> {
    return this.http
      .get<{ url: string; hasToken: boolean }>(`${this.jobsUrl}/api/mail/webhook`, { headers: this.credentials() })
      .pipe(catchError(() => of(null)));
  }

  private get jobsUrl(): string {
    return this.appConfig.get<string>('jobs.baseUrl', '/jobs-api');
  }

  /** The sidecar checks the caller with Alfresco; a ticket travels in the ROLE_TICKET form. */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }

  /**
   * Whether the chosen channel has enough configuration to actually deliver.
   * `mailto` always can — it opens the user's own mail app.
   */
  static isConfigured(config: MailConfig): boolean {
    switch (config.mode) {
      case 'alfresco':
        return true;
      case 'emailjs':
        return !!(config.emailjs.serviceId && config.emailjs.templateId && config.emailjs.publicKey);
      case 'webhook':
        return /^https?:\/\//.test(config.webhook.url);
      default:
        return true;
    }
  }

  /**
   * Sends the message through the configured channel. For `mailto` this only
   * reports back — the caller opens the draft itself. `config` overrides the
   * stored settings so the settings screen can test unsaved values.
   */
  send(request: MailRequest, config: MailConfig = this.settings()): Observable<MailMode> {
    switch (config.mode) {
      case 'alfresco':
        return this.sendViaAlfresco(request);
      case 'emailjs':
        return this.sendViaEmailJs(request, config);
      case 'webhook':
        return this.sendViaWebhook(request, config);
      default:
        return of('mailto' as MailMode);
    }
  }

  /** The repository's own `mail` action — needs outbound SMTP on the server. */
  private sendViaAlfresco(request: MailRequest): Observable<MailMode> {
    const actions = new ActionsApi(this.api.getInstance() as any);
    return from(
      actions.actionExec({
        actionDefinitionId: 'mail',
        targetId: request.vars?.['nodeId'],
        params: { to: request.to, subject: request.subject, text: request.body }
      }) as Promise<any>
    ).pipe(map(() => 'alfresco' as MailMode));
  }

  private sendViaEmailJs(request: MailRequest, config: MailConfig): Observable<MailMode> {
    const payload = {
      service_id: config.emailjs.serviceId,
      template_id: config.emailjs.templateId,
      user_id: config.emailjs.publicKey,
      template_params: {
        to_email: request.to,
        subject: request.subject,
        message: request.body,
        from_name: config.fromName || undefined,
        ...(request.vars || {})
      }
    };
    return from(
      fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then((res) => {
        if (!res.ok) {
          throw new Error(`EmailJS ${res.status}`);
        }
        return 'emailjs' as MailMode;
      })
    );
  }

  /** The sidecar posts to the saved webhook with the token only it holds. */
  private sendViaWebhook(request: MailRequest, config: MailConfig): Observable<MailMode> {
    return this.http
      .post(
        `${this.jobsUrl}/api/mail/send`,
        { to: request.to, subject: request.subject, body: request.body, fromName: config.fromName || null, vars: request.vars ?? null },
        { headers: this.credentials() }
      )
      .pipe(map(() => 'webhook' as MailMode));
  }

  static sanitize(config: MailConfig): MailConfig {
    const mode = MAIL_MODES.includes(config?.mode) ? config.mode : 'mailto';
    return {
      mode,
      fromName: (config?.fromName || '').slice(0, 100),
      emailjs: {
        serviceId: (config?.emailjs?.serviceId || '').slice(0, 200),
        templateId: (config?.emailjs?.templateId || '').slice(0, 200),
        publicKey: (config?.emailjs?.publicKey || '').slice(0, 200)
      },
      webhook: {
        url: (config?.webhook?.url || '').slice(0, 500),
        token: (config?.webhook?.token || '').slice(0, 500)
      }
    };
  }

  private read(): Observable<MailConfig | null> {
    return this.nodeId(false).pipe(
      switchMap((nodeId) => (nodeId ? from(this.nodes().getNodeContent(nodeId) as Promise<any>) : of(null))),
      switchMap((content: any) => (content ? this.text(content) : of(null))),
      map((text) => {
        if (!text) {
          return null;
        }
        try {
          const saved = MailService.sanitize({ ...MailService.DEFAULT, ...(JSON.parse(text) as MailConfig) });
          // a token from before it moved to the sidecar: held back from the
          // page and handed over the next time an administrator saves
          this.legacyToken = saved.webhook.token;
          return { ...saved, webhook: { url: saved.webhook.url, token: '' } };
        } catch {
          return null;
        }
      })
    );
  }

  private text(content: any): Observable<string | null> {
    if (typeof content === 'string') {
      return of(content);
    }
    if (content instanceof Blob) {
      return from(content.text());
    }
    return of(null);
  }

  private nodeId(create: boolean): Observable<string | null> {
    const nodes = this.nodes();
    return from(nodes.getNode('-root-', { relativePath: MailService.NODE_PATH }) as Promise<any>).pipe(
      map((entry: any) => entry?.entry?.id as string),
      catchError(() => {
        if (!create) {
          return of(null);
        }
        return from(nodes.createNode('-root-', { name: 'ACA', nodeType: 'cm:folder', relativePath: 'Data Dictionary' } as any) as Promise<any>).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            from(
              nodes.createNode('-root-', {
                name: MailService.FILE_NAME,
                nodeType: 'cm:content',
                relativePath: MailService.FOLDER_PATH
              } as any) as Promise<any>
            )
          ),
          map((entry: any) => entry?.entry?.id as string)
        );
      })
    );
  }

  private nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }
}
